import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import WavyText from "../../../components/animations/WavyText.jsx";
import AnimatedHeading from "../../../components/animations/AnimatedHeading.jsx";
import Noise from "../../../components/animations/Noise.jsx";

import { blogs } from "../../../components/User/Blog/blogData";

export default function BlogSection() {
  const navigate = useNavigate();

  // ============================================================
  // HOMEPAGE BLOGS
  // ============================================================

  const homeBlogs = blogs.slice(0, 3);

  // ============================================================
  // NAVIGATION
  // ============================================================

  const goToBlog = (slug) => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    navigate(`/blogs/${slug}`);
  };

  const goToAllBlogs = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    navigate("/blogs");
  };

  return (
    <section
      className="
        relative
        w-full
        overflow-hidden
        theme-bg-primary
        px-3
        py-5
        font-saira
        theme-text-primary
        transition-colors
        duration-500

        min-[360px]:px-4
        min-[360px]:py-6

        sm:px-5
        sm:py-7

        md:px-7
        md:py-9

        lg:px-10
        lg:py-10

        xl:px-12
        xl:py-11

        2xl:px-16
        2xl:py-12
      "
    >
      {/* ================================================================ */}
      {/* NOISE BACKGROUND                                                 */}
      {/* ================================================================ */}

      <div
        className="
          pointer-events-none
          absolute
          inset-0
          z-[1]
          opacity-60
        "
      >
        <Noise
          patternSize={250}
          patternScaleX={2}
          patternScaleY={2}
          patternRefreshInterval={2}
          patternAlpha={12}
        />
      </div>

      {/* ================================================================ */}
      {/* ORANGE GLOW - RIGHT                                              */}
      {/* ================================================================ */}

      <div
        className="
          pointer-events-none
          absolute
          -right-24
          top-12
          h-40
          w-40
          rounded-full
          bg-primaryColor/[0.035]
          blur-[85px]

          min-[480px]:-right-28
          min-[480px]:h-48
          min-[480px]:w-48

          md:-right-32
          md:top-16
          md:h-56
          md:w-56

          lg:h-64
          lg:w-64

          xl:h-72
          xl:w-72
        "
      />

      {/* ================================================================ */}
      {/* ORANGE GLOW - LEFT                                               */}
      {/* ================================================================ */}

      <div
        className="
          pointer-events-none
          absolute
          -left-24
          bottom-8
          h-36
          w-36
          rounded-full
          bg-primaryColor/[0.025]
          blur-[85px]

          min-[480px]:h-44
          min-[480px]:w-44

          md:-left-28
          md:h-52
          md:w-52

          lg:h-60
          lg:w-60

          xl:h-64
          xl:w-64
        "
      />

      {/* ================================================================ */}
      {/* MAIN CONTAINER                                                   */}
      {/* ================================================================ */}

      <div
        className="
          relative
          z-10
          mx-auto
          w-full
          max-w-[1500px]
        "
      >
        {/* ============================================================ */}
        {/* HEADER                                                        */}
        {/* ============================================================ */}

        <div
          className="
            mb-5
            flex
            flex-col
            gap-4

            min-[480px]:mb-6

            sm:gap-5

            md:mb-7
            md:flex-row
            md:items-end
            md:justify-between
            md:gap-6

            lg:mb-8

            xl:mb-9
          "
        >
          {/* HEADER TEXT */}

          <div className="min-w-0 flex-1">
            <p
              className="
                text-[8px]
                font-bold
                uppercase
                tracking-[0.22em]
                text-primaryColor

                min-[360px]:text-[9px]

                sm:text-[10px]

                md:text-xs
              "
            >
              [ Our Latest Blogs ]
            </p>

            <AnimatedHeading
              as="h2"
              parts={["Explore Latest Insights, &", "Innovations."]}
              className="
                mt-2
                max-w-[850px]
                text-[25px]
                font-semibold
                leading-[1.05]
                tracking-tight
                theme-text-primary

                min-[360px]:text-[27px]

                min-[400px]:text-[29px]

                sm:mt-2.5
                sm:text-3xl

                md:text-4xl

                lg:text-[44px]
                lg:leading-[1.02]

                xl:text-5xl
              "
            />
          </div>

          {/* EXPLORE MORE BUTTON */}

          <button
            type="button"
            onClick={goToAllBlogs}
            className="
              group
              relative
              inline-flex
              w-fit
              shrink-0
              overflow-hidden
              rounded-full
              border
              border-primaryColor
              bg-primaryColor
              px-2.5
              py-1.5
              font-semibold
              text-black
              shadow-[0_8px_25px_rgba(230,112,0,0.12)]
              transition-all
              duration-300
              hover:scale-[1.03]
              hover:text-white
              active:scale-95

              sm:px-3
              sm:py-1.5

              md:px-3.5
              md:py-2

              lg:px-4
              lg:py-2
            "
          >
            {/* Hover Background */}

            <span
              aria-hidden="true"
              className="
                absolute
                inset-0
                w-0
                bg-black
                transition-all
                duration-500
                ease-out
                group-hover:w-full
              "
            />

            {/* Button Content */}

            <span
              className="
                relative
                z-10
                flex
                items-center
                gap-1.5
                text-[9px]

                min-[360px]:text-[10px]

                sm:gap-2
                sm:text-xs

                md:text-sm
              "
            >
              <WavyText text="Explore More" />

              <span
                className="
                  inline-flex
                  h-5
                  w-5
                  items-center
                  justify-center
                  rounded-full
                  bg-black
                  text-white
                  transition-all
                  duration-500
                  group-hover:translate-x-1
                  group-hover:rotate-45
                  group-hover:bg-primaryColor
                  group-hover:text-black

                  min-[360px]:h-6
                  min-[360px]:w-6

                  sm:h-7
                  sm:w-7

                  md:h-8
                  md:w-8
                "
              >
                <ArrowUpRight
                  className="
                    h-3
                    w-3

                    min-[360px]:h-3.5
                    min-[360px]:w-3.5

                    sm:h-4
                    sm:w-4

                    md:h-[17px]
                    md:w-[17px]
                  "
                  strokeWidth={2.2}
                />
              </span>
            </span>
          </button>
        </div>

        {/* ============================================================ */}
        {/* BLOG LIST                                                     */}
        {/* ============================================================ */}

        <div
          className="
            relative
            z-10
            space-y-2.5

            min-[480px]:space-y-3

            sm:space-y-3.5

            md:space-y-4

            lg:space-y-4
          "
        >
          {homeBlogs.map((blog, index) => {
            const date = new Date(blog.date);

            const validDate = !isNaN(date.getTime());

            const day = validDate
              ? date.getDate().toString().padStart(2, "0")
              : blog.date.split(" ")[0];

            const month = validDate
              ? date
                  .toLocaleString("en-US", {
                    month: "short",
                  })
                  .toUpperCase()
              : blog.date.split(" ")[1]?.toUpperCase() || "";

            return (
              <motion.article
                key={blog.id}
                initial={{
                  opacity: 0,
                  y: 12,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                  amount: 0.12,
                }}
                transition={{
                  duration: 0.45,
                  delay: index * 0.07,
                  ease: "easeOut",
                }}
                whileHover={{
                  y: -3,
                }}
                onClick={() => goToBlog(blog.slug)}
                className="
                  group
                  relative
                  flex
                  w-full
                  cursor-pointer
                  items-center
                  gap-2
                  overflow-hidden
                  rounded-xl
                  border
                  theme-border
                  theme-card
                  p-2.5
                  shadow-[0_10px_30px_var(--shadow-color)]
                  transition-all
                  duration-300
                  hover:border-primaryColor/50

                  min-[360px]:gap-2.5
                  min-[360px]:p-3

                  min-[400px]:gap-3

                  sm:gap-4
                  sm:rounded-2xl
                  sm:p-3.5

                  md:gap-5
                  md:p-4

                  lg:p-4.5

                  xl:p-5
                "
              >
                {/* ====================================================== */}
                {/* HOVER GLOW                                               */}
                {/* ====================================================== */}

                <div
                  className="
                    pointer-events-none
                    absolute
                    -right-16
                    -top-16
                    h-32
                    w-32
                    rounded-full
                    bg-primaryColor/[0.045]
                    opacity-0
                    blur-[60px]
                    transition-opacity
                    duration-500
                    group-hover:opacity-100

                    sm:-right-20
                    sm:-top-20
                    sm:h-40
                    sm:w-40
                  "
                />

                {/* ====================================================== */}
                {/* LEFT CONTENT                                              */}
                {/* ====================================================== */}

                <div
                  className="
                    flex
                    min-w-0
                    flex-1
                    items-start
                    gap-2

                    min-[360px]:gap-2.5

                    min-[400px]:gap-3

                    sm:gap-4

                    md:gap-5
                  "
                >
                  {/* DATE */}

                  <div className="shrink-0">
                    <div
                      className="
                        flex
                        h-12
                        w-10
                        flex-col
                        items-center
                        justify-center
                        rounded-lg
                        bg-primaryColor
                        font-bold
                        text-black
                        shadow-[0_0_18px_rgba(230,112,0,0.20)]

                        min-[360px]:h-13
                        min-[360px]:w-11

                        min-[400px]:h-14
                        min-[400px]:w-12

                        sm:h-16
                        sm:w-14
                        sm:rounded-xl

                        md:h-[66px]
                        md:w-[55px]

                        lg:h-[70px]
                        lg:w-[58px]
                      "
                    >
                      <div
                        className="
                          text-lg
                          leading-none

                          min-[360px]:text-xl

                          sm:text-2xl

                          md:text-[25px]
                        "
                      >
                        {day}
                      </div>

                      <div
                        className="
                          mt-0.5
                          text-[7px]
                          leading-none

                          min-[360px]:text-[8px]

                          sm:text-[9px]

                          md:text-[10px]
                        "
                      >
                        {month}
                      </div>
                    </div>
                  </div>

                  {/* BLOG CONTENT */}

                  <div className="min-w-0 flex-1">
                    {/* META */}

                    <div
                      className="
                        mb-1
                        flex
                        min-w-0
                        flex-wrap
                        items-center
                        gap-1
                        text-[7px]
                        theme-text-muted

                        min-[360px]:text-[8px]

                        sm:mb-1.5
                        sm:gap-1.5
                        sm:text-[9px]

                        md:text-[10px]
                      "
                    >
                      <span
                        className="
                          max-w-[110px]
                          truncate
                          rounded-md
                          border
                          theme-border
                          theme-bg-tertiary
                          px-1
                          py-0.5
                          font-semibold
                          text-primaryColor

                          min-[360px]:px-1.5

                          sm:px-2
                        "
                      >
                        {blog.category}
                      </span>

                      <span className="min-w-0 truncate">
                        By {blog.author}
                      </span>
                    </div>

                    {/* TITLE */}

                    <h3
                      className="
                        max-w-[900px]
                        break-words
                        text-[13px]
                        font-semibold
                        leading-[1.3]
                        theme-text-primary

                        min-[360px]:text-[14px]

                        min-[400px]:text-[15px]

                        sm:text-lg
                        sm:leading-snug

                        md:text-xl

                        lg:text-[21px]

                        xl:text-[22px]
                      "
                    >
                      <WavyText text={blog.title} />
                    </h3>

                    {/* READ MORE */}

                    <motion.button
                      type="button"
                      whileHover={{
                        x: 4,
                      }}
                      transition={{
                        duration: 0.2,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        goToBlog(blog.slug);
                      }}
                      className="
                        mt-1
                        inline-flex
                        items-center
                        gap-1
                        text-[8px]
                        font-medium
                        theme-text-secondary
                        transition-colors
                        duration-300
                        hover:text-primaryColor

                        min-[360px]:text-[9px]

                        sm:mt-2
                        sm:text-[10px]

                        md:text-xs
                      "
                    >
                      Read More

                      <ArrowUpRight
                        className="
                          h-2.5
                          w-2.5

                          min-[360px]:h-3
                          min-[360px]:w-3

                          sm:h-3.5
                          sm:w-3.5
                        "
                        strokeWidth={2}
                      />
                    </motion.button>
                  </div>
                </div>

                {/* ====================================================== */}
                {/* RIGHT IMAGE                                              */}
                {/* ====================================================== */}

                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    goToBlog(blog.slug);
                  }}
                  className="
                    relative
                    hidden
                    shrink-0
                    overflow-hidden
                    rounded-lg
                    ring-1
                    ring-black/[0.08]

                    sm:block
                    sm:h-[76px]
                    sm:w-[112px]

                    md:h-[92px]
                    md:w-[145px]
                    md:rounded-xl

                    lg:h-[105px]
                    lg:w-[165px]

                    xl:h-[115px]
                    xl:w-[185px]

                    2xl:h-[125px]
                    2xl:w-[200px]
                  "
                >
                  <motion.img
                    src={blog.image}
                    alt={blog.title}
                    loading="lazy"
                    draggable={false}
                    whileHover={{
                      scale: 1.06,
                    }}
                    transition={{
                      duration: 0.45,
                      ease: "easeOut",
                    }}
                    className="
                      h-full
                      w-full
                      object-cover
                    "
                  />

                  {/* Image Overlay */}

                  <div
                    className="
                      pointer-events-none
                      absolute
                      inset-0
                      bg-gradient-to-t
                      from-black/45
                      via-transparent
                      to-transparent
                      opacity-60
                    "
                  />

                  {/* Image Arrow */}

                  <div
                    className="
                      absolute
                      bottom-2
                      right-2
                      flex
                      h-6
                      w-6
                      items-center
                      justify-center
                      rounded-full
                      bg-black/70
                      text-white
                      opacity-0
                      backdrop-blur-sm
                      transition-all
                      duration-300
                      group-hover:opacity-100

                      md:h-7
                      md:w-7
                    "
                  >
                    <ArrowUpRight
                      className="
                        h-3
                        w-3

                        md:h-3.5
                        md:w-3.5
                      "
                    />
                  </div>
                </div>

                {/* ====================================================== */}
                {/* MOBILE ARROW                                             */}
                {/* ====================================================== */}

                <div
                  className="
                    flex
                    h-6
                    w-6
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    border
                    theme-border
                    theme-bg-tertiary
                    theme-text-secondary
                    transition-all
                    duration-300
                    group-hover:border-primaryColor
                    group-hover:bg-primaryColor
                    group-hover:text-black

                    min-[360px]:h-7
                    min-[360px]:w-7

                    sm:hidden
                  "
                >
                  <ArrowUpRight
                    className="
                      h-3
                      w-3

                      min-[360px]:h-3.5
                      min-[360px]:w-3.5
                    "
                    strokeWidth={2}
                  />
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}